import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { filter, map, Observable, switchMap } from 'rxjs';
import { SetPendingSave } from '../classification/classification.actions';
import { ClassificationState } from '../classification/classification.state';
import { DialogComponent } from '../dialog/dialog.component';
import { CANCEL_BUTTON, DialogData } from '../dialog/dialog.model';
import { DialogService } from '../dialog/dialog.service';
import { FeatureFlagKey } from '../feature-flags/feature-flag-key';
import { ClassificationComponent } from './classification/classification.component';
import { ConfigTab } from './config-tab';
import { SetActiveConfigTab } from './config.actions';
import { ConfigState } from './config.state';
import { configTranslations } from './config.translations';
import { PermissionsComponent } from './permissions/permissions.component';
import { RecycleBinComponent } from './recycle-bin/recycle-bin.component';
import { StorageComponent } from './storage/storage.component';

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    MatTabsModule,
    StorageComponent,
    RecycleBinComponent,
    ClassificationComponent,
    PermissionsComponent,
  ],
  templateUrl: './config.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 100%;
        max-height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly featureFlagService: FeatureFlagService<FeatureFlagKey> =
    inject(FeatureFlagService);

  private readonly tabGroup = viewChild.required(MatTabGroup);

  readonly translations = configTranslations();

  private readonly tabs = {
    [ConfigTab.Storage]: 0,
    [ConfigTab.RecycleBin]: 1,
    [ConfigTab.Classification]: 2,
    [ConfigTab.Permissions]: 3,
  };

  readonly activeTab = toSignal(this.store.select(ConfigState.activeTab), { requireSync: true });
  readonly selectedTabIndex = computed(() => this.tabs[this.activeTab()]);

  readonly showPermissionsTab = toSignal(
    this.featureFlagService.hasFeature$('tools_settings.permissions'),
    { initialValue: false },
  );

  readonly queryParams = toSignal(this.route.queryParams, { requireSync: true });
  readonly showBackNavigation = computed(() => !!this.queryParams()['showBackNavigation']);

  private readonly pendingSave = this.store.selectSignal(ClassificationState.pendingSave);
  private discardDialogRef?: MatDialogRef<DialogComponent, boolean>;

  constructor() {
    this.addTabClickHandler();
  }

  ngOnDestroy(): void {
    this.discardDialogRef?.close();
  }

  viewScandata() {
    if (!this.pendingSave()) {
      this.router.navigate(['/']);
      return;
    }

    this.discardChanges().subscribe(() => this.router.navigate(['/']));
  }

  tabChange(event: MatTabChangeEvent) {
    this.setActiveTab(event.index);
  }

  private setActiveTab(index: number) {
    const tab = Object.keys(this.tabs)[index] as ConfigTab;
    this.store.dispatch(new SetActiveConfigTab(tab));
  }

  private addTabClickHandler() {
    toObservable(this.tabGroup).subscribe((tabGroup) => {
      // Material does not expose a way to cancel tab change other than overriding this private method.
      tabGroup._handleClick = (tab, header, index) => {
        if (this.selectedTabIndex() === index) return;
        if (!this.pendingSave()) return this.setActiveTab(index);

        this.discardChanges().subscribe(() => this.setActiveTab(index));
      };
    });
  }

  private discardChanges(): Observable<boolean> {
    const dialogData = new DialogData(
      this.translations.discardChanges.title.text,
      this.translations.discardChanges.message.text,
      { text: this.translations.discardChanges.confirm.text, color: 'danger' },
      CANCEL_BUTTON,
    );

    this.discardDialogRef = this.dialogService.showMessage(dialogData);

    return this.discardDialogRef.afterClosed().pipe(
      filter((discard) => discard ?? false),
      switchMap(() => this.store.dispatch(new SetPendingSave(false))),
      map(() => true),
    );
  }
}
